import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PencilIcon } from "../Auth/onboarding/icons";
import { ExternalLinkIcon } from "./icons";
import { getAssistantConfig, getAssistantById } from "../../actions/assistants";
import { RoutesPaths } from "../../Router/constant";
import { hostOf } from "../../utils/url";
import { readWidgetTheme } from "../../utils/widgetTheme";
import WidgetGlyph from "../../Components/WidgetGlyph";
import WidgetPreview from "../Customization/WidgetPreview";
import { configToSettings } from "../Customization/mapConfig";
import { defaultSettings } from "../Customization/defaultSettings";

export default function AssistantLiveCard() {
  const assistantId = useSelector((state) => state.assistants.activeId);
  // The sidebar switcher may not have loaded its list yet, so the record is
  // fetched below rather than relied on from here.
  const listed = useSelector((state) =>
    state.assistants.list.find((item) => item.id === state.assistants.activeId),
  );
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [record, setRecord] = useState(null);
  // Starts true when there is an assistant to fetch, so the first paint is the
  // skeleton rather than app defaults that visibly swap a moment later.
  const [loading, setLoading] = useState(Boolean(assistantId));

  // Two requests, because neither payload is the whole picture:
  //   /config          the widget theme visitors actually see
  //   /assistants/:id  status and sourceUrl, which /config omits
  useEffect(() => {
    if (!assistantId) {
      setConfig(null);
      setRecord(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      dispatch(getAssistantConfig(assistantId)),
      dispatch(getAssistantById(assistantId)),
    ]).then(([configResult, recordResult]) => {
      if (cancelled) return;
      if (configResult?.success) setConfig(configResult.config);
      if (recordResult?.success) setRecord(recordResult.assistant);
      // Cleared on failure too: the card falls back to defaults, which is
      // still better than a skeleton that never resolves.
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [assistantId, dispatch]);

  const assistant = record || listed;
  // Flat theme, whichever shape the API sent.
  const theme = readWidgetTheme(config);
  // The same translation the Customization page does, so the widget below is
  // rendered by the same component from the same fields - every colour, font,
  // radius, spacing and toggle in the config, not just the handful this card
  // used to read.
  const settings = configToSettings(config, defaultSettings);

  const name = config?.name || assistant?.name || "Your assistant";
  const status = assistant?.status;
  // An assistant the plan no longer has room for is kept, but switched off:
  // its widget stops loading until the account takes a bigger plan or removes
  // another assistant.
  const paused = Boolean(assistant?.pausedAt);
  const overPlanLimit = assistant?.pausedReason === "plan_limit";
  // A re-crawl runs in the background; the assistant keeps answering while it
  // does, so this is a note rather than a state.
  const recrawling = Boolean(assistant?.recrawlStartedAt);
  // Unknown until the record arrives; only "ready" and not paused is live.
  const isLive = status ? status === "ready" && !paused : !assistant;
  const sourceUrl = assistant?.sourceUrl;
  const host = hostOf(sourceUrl);

  if (loading) {
    return (
      <section className="dash-card dash-live-card" aria-busy="true">
        <div className="dash-card-header">
          <span className="skel dash-live-skel--title" />
        </div>

        <div className="dash-live-identity">
          <span className="skel dash-live-skel--avatar" />
          <div className="dash-live-identity-text">
            <span className="skel dash-live-skel--name" />
            <span className="skel dash-live-skel--sub" />
          </div>
        </div>

        <span className="skel dash-live-skel--widget" />
        <span className="skel dash-live-skel--link" />
      </section>
    );
  }

  return (
    <section className="dash-card dash-live-card">
      <div className="dash-card-header">
        <h2 className="dash-card-title">
          {isLive ? "Your assistant is live" : "Your assistant"}
        </h2>
        {paused ? (
          <span className="dash-live-badge is-paused">
            {overPlanLimit ? "Paused — over plan limit" : "Paused"}
          </span>
        ) : isLive ? (
          <span className="dash-live-badge">
            <span className="dash-live-dot" />
            Live
          </span>
        ) : (
          status && <span className="dash-live-badge is-pending">{status}</span>
        )}
      </div>

      {/* Why it is off, and what to do about it. The plan page is where both
          ways out of a plan_limit pause are. */}
      {paused && (
        <p className="dash-live-note is-warn">
          {overPlanLimit
            ? "Your plan has room for fewer assistants than you have, so this one is switched off. Take a bigger plan, or remove another assistant."
            : "This assistant is switched off, so its widget is not loading."}
          {overPlanLimit && (
            <Link to={RoutesPaths.BILLING}>See plans</Link>
          )}
        </p>
      )}

      {!paused && recrawling && (
        <p className="dash-live-note">
          Updating content… it keeps answering from what it already knows.
        </p>
      )}

      {assistant?.lastCrawlError && !paused && (
        <p className="dash-live-note is-warn">{assistant.lastCrawlError}</p>
      )}

      <div className="dash-live-identity">
        <span
          className="dash-live-avatar"
          style={
            // An uploaded image stands alone; the tint only backs a glyph.
            theme.avatarUrl || !settings.primaryColor
              ? undefined
              : { background: settings.primaryColor }
          }
        >
          <WidgetGlyph theme={theme} size={20} />
        </span>
        <div className="dash-live-identity-text">
          <strong>{name}</strong>
          <span>
            {config?.tagline ||
              (host ? `AI Receptionist for ${host}` : "AI Receptionist")}
          </span>
        </div>
        <Link
          to={RoutesPaths.CUSTOMIZATION}
          className="dash-live-icon-btn"
          aria-label="Edit assistant"
        >
          <PencilIcon />
        </Link>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="dash-live-icon-btn"
            aria-label="Open website"
          >
            <ExternalLinkIcon />
          </a>
        )}
      </div>

      <div className="dash-live-widget">
        <WidgetPreview settings={settings} />
      </div>

      {/* <Link to={RoutesPaths.ASSISTANT} className="dash-live-preview-link">
        Preview full widget
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
        >
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link> */}
    </section>
  );
}
